import { z } from "zod";
import {
  isoDateString,
  isoDateTimeString,
  phoneSchema,
  uuidSchema,
} from "./common";
import { kycStatusSchema } from "./kyc";
import { orgRoleSchema } from "./users";
import { PROVINCE_CODES, SUPPORTED_PROVINCE_CODES } from "../catalogs";
import { orgAddressSchema } from "./org-addresses";

// Hard cap on the org public gallery. Enforced in zod (request + entity
// schemas) and re-checked server-side in OrgsService.update.
export const MAX_ORG_GALLERY_IMAGES = 10;

// Address fields used for the primary branch (and any subsequent OrgAddress
// create / update). Re-used by `createOrgRequestSchema.primaryAddress` and
// by the OrgAddress endpoints (see `org-addresses.ts`).
const addressCoreFields = {
  address: z.string().min(1).max(255),
  // Legacy admin fields — geocoder-filled display strings, now optional (ward
  // replaces district). Kept so the write path fills the still-NOT-NULL columns.
  district: z.string().max(60).optional().default(""),
  city: z.string().max(60).optional().default(""),
  // Legacy province enum (63 old codes, HCM-only allowlist). Now optional +
  // nullable: it only resolves for HCM, but the app is nationwide via ward.
  // The authoritative province is `wardProvinceCode`.
  provinceCode: z.enum(SUPPORTED_PROVINCE_CODES).nullable().optional(),
  // 2025 ward — REQUIRED (decision: bắt buộc chọn phường). Auto-filled by
  // point-in-polygon and editable. `wardProvinceCode` is the official new
  // numeric province code string ("79" = HCM).
  wardCode: z.string().min(1, "Vui lòng chọn phường / xã").max(20),
  wardName: z.string().min(1).max(120),
  wardProvinceCode: z.string().min(1).max(10),
  lat: z.number(),
  lng: z.number(),
  // Source Google Maps URL — drives the branded "Open Maps" deep-link
  // on mobile. Optional: legacy / non-Google sources fall back to lat/lng.
  mapUrl: z.string().url().nullable().optional(),
};

// Legal business type (SCRUM-73). Distinct from businessType (F&B kind).
export const orgLegalTypeSchema = z.enum([
  "INDIVIDUAL",
  "HOUSEHOLD",
  "COMPANY",
]);

// Organization verification ladder (SCRUM-74). Replaces verifiedTier.
export const orgVerifyLevelSchema = z.enum([
  "L0_NEW",
  "L1_CONTACT",
  "L2_DOCUMENT",
  "L3_TRUSTED",
]);

export const orgSchema = z.object({
  id: uuidSchema,
  // True when the org has ≥1 active branch on the pre-2025 address model (no
  // ward). Drives the "cập nhật địa chỉ theo phường/xã mới" banner. Defaults
  // false; only the single-org detail endpoint computes it.
  addressNeedsWardUpdate: z.boolean().optional().default(false),
  // Nullable: shadow orgs created by admin have no owner until claimed via
  // OrgClaimInvitation. Live (claimed) orgs always have an owner.
  ownerId: uuidSchema.nullable(),
  name: z.string(),
  businessType: z.string(),
  taxCode: z.string().nullable().optional(),
  // Legal identity (SCRUM-73). legalType always present (defaults INDIVIDUAL);
  // the rest null until the employer fills the legal form.
  legalType: orgLegalTypeSchema,
  legalName: z.string().nullable().optional(),
  legalRegNo: z.string().nullable().optional(),
  legalRegDate: isoDateString.nullable().optional(),
  legalAddress: z.string().nullable().optional(),
  legalProvinceCode: z.enum(PROVINCE_CODES).nullable().optional(),
  legalRepUserId: uuidSchema.nullable().optional(),
  // Primary branch — set on org create, can be re-pointed by the owner.
  // Nullable on the wire because DB column is nullable (ON DELETE SET NULL)
  // but in practice always present for live orgs.
  primaryAddressId: uuidSchema.nullable(),
  primaryAddress: orgAddressSchema.nullable(),
  contactName: z.string(),
  contactPhone: z.string(),
  // Contact email for the org. For shadow orgs this is where cold claim-invite
  // + periodic FOMO digest emails are sent (separate from the owner's account
  // email). Null when unset. Admin-editable via PATCH /orgs/:id.
  contactEmail: z.string().nullable().optional(),
  logoImageKey: z.string().nullable().optional(),
  // Resolved CDN URL for the logo (org "avatar"). Serializer always emits it
  // (null when unset); declared here so the SDK's strict parse keeps it for
  // the employer/admin preview. Worker mobile reads it via `orgPublicSchema`.
  logoPublicUrl: z.string().nullable(),
  bannerImageKey: z.string().nullable().optional(),
  bannerPublicUrl: z.string().nullable(),
  // Ordered R2 object keys for the public gallery (max 10). Editing surfaces
  // need the raw keys; `galleryPublicUrls` carries the resolved CDN URLs.
  galleryImageKeys: z.array(z.string()).max(MAX_ORG_GALLERY_IMAGES),
  galleryPublicUrls: z.array(z.string()),
  kycStatus: kycStatusSchema,
  kycApprovedAt: isoDateTimeString.nullable().optional(),
  kycRejectedAt: isoDateTimeString.nullable().optional(),
  kycRejectReason: z.string().nullable().optional(),
  // Admin-assigned trust tier (badge on the worker feed).
  // Deprecated (SCRUM-74) — kept one cycle. Use verifyLevel.
  verifiedTier: z.enum(["NONE", "TRUSTED", "SUPER"]).default("NONE"),
  verifyLevel: orgVerifyLevelSchema.default("L0_NEW"),
  inviteCreditsAvailable: z.number().int(),
  inviteCreditsLocked: z.number().int(),
  freeTrialGrantedAt: isoDateTimeString.nullable().optional(),
  kycBonusGrantedAt: isoDateTimeString.nullable().optional(),
  referralCode: z.string().nullable().optional(),
  referredByCode: z.string().nullable().optional(),
  isShadow: z.boolean().optional(),
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
});

// Legal-entity fields. All optional at the DTO layer because the employer may
// "skip, fill later" during onboarding (spec §5.3/5.4). INDIVIDUAL never fills
// these. Server enforces the legalRep / required-field rules per legal type.
const orgLegalFields = {
  legalName: z.string().max(200).nullable().optional(),
  legalRegNo: z.string().max(50).nullable().optional(),
  legalRegDate: isoDateString.nullable().optional(),
  legalAddress: z.string().max(300).nullable().optional(),
  legalProvinceCode: z.enum(PROVINCE_CODES).nullable().optional(),
};

const orgCoreFields = {
  name: z.string().min(1).max(120),
  businessType: z.string().min(1).max(60),
  taxCode: z.string().max(20).nullable().optional(),
  contactName: z.string().min(1).max(120),
  contactPhone: phoneSchema,
};

export const createOrgRequestSchema = z
  .object(orgCoreFields)
  .extend(orgLegalFields)
  .extend({
    // Chosen at onboarding step 1. Optional on the wire (defaults INDIVIDUAL
    // server-side) so older clients keep working.
    legalType: orgLegalTypeSchema.optional(),
    // The first branch (becomes the org's primaryAddress). Created in the
    // same transaction as Organization.
    primaryAddress: z.object(addressCoreFields),
    // Optional referral code entered at signup. Normalised to upper-case
    // on the API side; we accept anything 4-12 chars to stay forgiving.
    referredByCode: z.string().min(4).max(12).optional(),
  });

// Updating Organization itself only touches non-address fields. Branch
// edits go through PATCH /org-addresses/:id; pointing primary at a
// different branch uses PATCH /orgs/:id { primaryAddressId }.
export const updateOrgRequestSchema = z
  .object(orgCoreFields)
  .partial()
  .extend(orgLegalFields)
  .extend({
    // Owner can change the legal type (subject to server rules / rate limits
    // wired in later tickets). Legal fields above are editable here too.
    legalType: orgLegalTypeSchema.optional(),
    primaryAddressId: uuidSchema.optional(),
    // Contact email (mainly for shadow orgs — the address that receives the
    // cold claim-invite + periodic FOMO digest). Send an email to set it, null
    // to clear, omit to leave untouched. Normalised to lower-case server-side.
    contactEmail: z.string().email().nullable().optional(),
    // Org logo ("avatar"). Send the R2 object key returned by the logo presign
    // to set it; send null to clear; omit to leave untouched.
    logoImageKey: z.string().nullable().optional(),
    // Replaces the whole ordered gallery in one PATCH. Omit to leave it
    // untouched; send [] to clear. Used by both employer and admin edit.
    galleryImageKeys: z
      .array(z.string())
      .max(MAX_ORG_GALLERY_IMAGES)
      .optional(),
    // Admin-only trust tier. The employer update path ignores it; only the
    // admin org-update service reads it.
    verifiedTier: z.enum(["NONE", "TRUSTED", "SUPER"]).optional(),
  });

// Presign one org gallery image upload. Mirrors the avatar presign shape:
// client sends content type + size, gets back a short-lived R2 PUT URL plus
// the object key + final public URL. Used by employer (own org) and admin
// (any org). Same JPEG/PNG/WebP + 10MB limits as avatars.
export const orgImagePresignRequestSchema = z.object({
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(10 * 1024 * 1024),
  // Which slot this upload targets — drives the R2 key namespace
  // (`org-logo/…` vs `org-gallery/…` vs `posting-preview/…`). Defaults to
  // gallery server-side so existing callers stay unchanged.
  purpose: z.enum(["gallery", "logo", "posting"]).optional(),
});

export const orgImagePresignResponseSchema = z.object({
  uploadUrl: z.string().url(),
  method: z.literal("PUT").optional(),
  headers: z.record(z.string(), z.string()).optional(),
  key: z.string(),
  publicUrl: z.string().url(),
  expiresAt: isoDateTimeString,
});

export const memberSchema = z.object({
  membershipId: uuidSchema,
  userId: uuidSchema,
  fullName: z.string().nullable(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable(),
  jobTitle: z.string().nullable().optional(),
  role: orgRoleSchema,
  joinedAt: isoDateTimeString,
  revokedAt: isoDateTimeString.nullable().optional(),
});

export const addMemberRequestSchema = z.object({
  email: z.string().email(),
  fullName: z.string().min(1).max(120),
  jobTitle: z.string().max(120).optional(),
  role: z.literal("HR"),
});

export const updateMemberRequestSchema = z.object({
  jobTitle: z.string().max(120).nullable().optional(),
  role: z.enum(["HR"]).optional(),
});

// Worker-facing public profile of an organization. Strips sensitive fields
// (contactPhone, ownerId, creditBalance, freeTrial timestamps, raw KYC
// review timestamps) and exposes a derived `kycVerified` boolean. Use
// this for the worker outlet-detail screen — workers are not org members
// and must not see admin/billing data. Owner contact phone is shared
// only after an invite is committed.
export const orgPublicSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  businessType: z.string(),
  taxCode: z.string().nullable(),
  // Primary branch — drives "Org · District" line on worker outlet card.
  primaryAddress: orgAddressSchema.nullable(),
  // All non-archived branches, primary first. Worker outlet-detail lists
  // every location (workers may interview at any branch), so this is the
  // full set, not just the primary.
  addresses: z.array(orgAddressSchema),
  contactName: z.string(),
  logoPublicUrl: z.string().nullable(),
  bannerPublicUrl: z.string().nullable(),
  // Ordered public gallery URLs for the worker outlet-detail photo viewer.
  // Empty array → no photo card is shown.
  galleryPublicUrls: z.array(z.string()),
  kycVerified: z.boolean(),
  createdAt: isoDateTimeString,
});

// Compact summary for the worker org-picker. Only orgs with at least one
// OPEN, non-expired posting appear here. `openPostingCount` lets the row
// show "(N việc)" without a second roundtrip.
export const orgSearchItemSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  district: z.string(),
  city: z.string(),
  logoPublicUrl: z.string().nullable(),
  kycVerified: z.boolean(),
  openPostingCount: z.number().int().min(0),
});

export const orgSearchQuerySchema = z.object({
  q: z.string().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});

export const orgSearchResponseSchema = z.object({
  items: z.array(orgSearchItemSchema),
  nextCursor: z.string().nullable(),
});

// Compact summary for the worker experience picker. A row here is a
// *branch* (OrgAddress), not the parent organization — workers worked
// at a specific location, not the legal entity. The branch carries its
// own alias + district so two Kocha branches show up as distinct rows.
export const orgBranchSearchItemSchema = z.object({
  id: uuidSchema, // orgAddressId
  orgId: uuidSchema,
  orgName: z.string(),
  alias: z.string(),
  district: z.string(),
  city: z.string(),
  logoPublicUrl: z.string().nullable(),
  kycVerified: z.boolean(),
});

export const orgBranchSearchResponseSchema = z.object({
  items: z.array(orgBranchSearchItemSchema),
  nextCursor: z.string().nullable(),
});

export type OrgSearchItem = z.infer<typeof orgSearchItemSchema>;
export type OrgSearchQuery = z.infer<typeof orgSearchQuerySchema>;
export type OrgBranchSearchItem = z.infer<typeof orgBranchSearchItemSchema>;
export type OrgBranchSearchResponse = z.infer<
  typeof orgBranchSearchResponseSchema
>;
export type OrgSearchResponse = z.infer<typeof orgSearchResponseSchema>;

export type OrgPublic = z.infer<typeof orgPublicSchema>;
export type Org = z.infer<typeof orgSchema>;
// ADMIN override of the computed verifyLevel (SCRUM-74). Reason mandatory.
export const adminOverrideVerifyLevelRequestSchema = z.object({
  level: orgVerifyLevelSchema,
  reason: z.string().trim().min(1, "Bắt buộc nhập lý do").max(500),
});

export type OrgLegalType = z.infer<typeof orgLegalTypeSchema>;
export type OrgVerifyLevel = z.infer<typeof orgVerifyLevelSchema>;
export type AdminOverrideVerifyLevelRequest = z.infer<
  typeof adminOverrideVerifyLevelRequestSchema
>;
export type CreateOrgRequest = z.infer<typeof createOrgRequestSchema>;
export type UpdateOrgRequest = z.infer<typeof updateOrgRequestSchema>;
export type OrgImagePresignRequest = z.infer<
  typeof orgImagePresignRequestSchema
>;
export type OrgImagePresignResponse = z.infer<
  typeof orgImagePresignResponseSchema
>;
export type Member = z.infer<typeof memberSchema>;
export type AddMemberRequest = z.infer<typeof addMemberRequestSchema>;
export type UpdateMemberRequest = z.infer<typeof updateMemberRequestSchema>;
